import { type FC, type PropsWithChildren, useMemo, type ReactNode } from 'react';

import { FlightInfo } from '@vakantiesnl/components/src/atoms/FlightInfo';
import { OfferItem } from '@vakantiesnl/components/src/molecules/OfferDetails/OfferItem';
import { OfferSummaryTemplate } from '@vakantiesnl/components/src/molecules/OfferSummary/OfferSummaryTemplate';
import { useGetDaysText, useGetNightsText } from '@vakantiesnl/services/src/hooks/utils/hook';
import { buildSummarizedPartyString } from '@vakantiesnl/services/src/util/partyUtils';
import { luggageGroupName } from '@vakantiesnl/services/src/util/persons';
import { inject } from '@vakantiesnl/services/src/util/template';
import { type MicroCopy, type Search, type Booking } from '@vakantiesnl/types';
import dayjs from 'dayjs';

import { useStyles } from './AccoOfferSummary.styles';
import { OfferSummaryWrapper } from '../../OfferSummaryWrapper';

export type OfferSummaryProps = {
	/** Nbc data */
	accommodation: Search.FullAccommodationItem;
	/** Party */
	party: Search.Party;
	/** Description of the mealplan */
	mealplanDescription?: string | null;
	/** Microcopies to render */
	microCopies: MicroCopy;
};

export type AccoOfferSummaryProps = OfferSummaryProps & {
	/** Function to check the availability */
	checkAvailability?: VoidFunction;
	/** Whether it's checking the availability */
	isCheckingAvailability?: boolean;
	/** Booking extra's */
	extras?: Booking.Extras;
	/** Duration in days */
	durationDays: number;
	/** Duration in nights */
	durationNights: number;
	/** Does the trip has a transfer */
	hasTransfer?: boolean;
	/** Does the trip has luggage included */
	hasLuggageIncluded?: boolean;
	/** Description of the room */
	roomDescription?: string | null;
	/** Inbound flight */
	inboundFlight?: Booking.Flight | Search.Flight;
	/** Outbound flight */
	outboundFlight?: Booking.Flight | Search.Flight;
	/** Should show price overview table */
	withPriceOverview?: boolean;
	/** Boolean used to show/hide info based on the offer */
	isAccoOnly?: boolean;
	/** TourOperator for the offer */
	tourOperator?: string;
	/** Departure date in YYYY-MM-DD of the offer */
	departureDate: string;
};

export const AccoOfferSummary: FC<PropsWithChildren<AccoOfferSummaryProps>> = ({
	accommodation,
	hasTransfer,
	hasLuggageIncluded,
	microCopies,
	extras,
	roomDescription,
	mealplanDescription,
	durationDays,
	durationNights,
	withPriceOverview,
	outboundFlight,
	inboundFlight,
	party,
	departureDate,
	isCheckingAvailability,
	checkAvailability,
	tourOperator,
	isAccoOnly,
	children,
}) => {
	const { classes, cx } = useStyles();

	const transferText = useMemo((): string => {
		if (hasTransfer) {
			return microCopies['transfer.inclusive'];
		}
		if (extras && extras.transfers.items.length > 0) {
			return inject(microCopies['common.booked'], {
				name: extras.transfers.items[0].productTypeName,
			}).join('');
		}

		return microCopies['transfer.exclusive'];
	}, [hasTransfer, microCopies, extras]);

	const luggageText = useMemo(() => {
		if (hasLuggageIncluded) {
			return microCopies['luggage.inclusive'];
		}
		if (extras && extras.luggage.items.length > 0) {
			return inject(microCopies['common.booked'], {
				name: luggageGroupName(
					'hold',
					extras.luggage.items[0].weight,
					false,
					extras.luggage.items[0].quantity,
					microCopies,
				),
			}).join('');
		}

		return microCopies['luggage.exclusive'];
	}, [hasLuggageIncluded, microCopies, extras]);

	const TopBlock = (): ReactNode => (
		<>
			<OfferItem
				variant="bold"
				iconType="destination"
				title={`${accommodation.country}, ${accommodation.region},	${accommodation.city}`}
				dataCy="OfferSummary-destination"
			/>
			<OfferItem variant="bold" iconType="calendar" title={departureDateText} dataCy="OfferSummary-duration" />
			<OfferItem
				variant="bold"
				iconType="familyRoom"
				title={buildSummarizedPartyString([party], microCopies)}
				dataCy="OfferSummary-pax"
			/>
			{roomDescription && (
				<OfferItem variant="bold" iconType="room" title={roomDescription} dataCy="OfferSummary-rooms" />
			)}
			{mealplanDescription && (
				<OfferItem variant="bold" iconType="food" title={mealplanDescription} dataCy="OfferSummary-mealplan" />
			)}
			{isAccoOnly && (
				<OfferItem
					variant="bold"
					iconType="car"
					title={microCopies['accoSummary.ownTransport']}
					dataCy="OfferSummary-own-transport"
				/>
			)}
		</>
	);

	const daysText = useGetDaysText(durationDays, false, microCopies);
	const nightsText = useGetNightsText(durationNights, microCopies);

	const departureDateText = useMemo(
		() => `${dayjs(departureDate, 'YYYY-MM-DD').format('dd D MMM YYYY')} (${daysText}, ${nightsText})`,
		[departureDate, daysText, nightsText],
	);

	const BottomBlock = (): ReactNode => (
		<>
			{!isAccoOnly && !!outboundFlight && !!inboundFlight && (
				<div>
					<div className={cx(classes.flights, classes.flightSection)}>
						<FlightInfo
							flight={outboundFlight}
							isOutbound={true}
							className={classes.flight}
							dataCy="OfferSummary-outboundFlight"
							showFlightDetails={true}
						/>
						<FlightInfo
							flight={inboundFlight}
							isOutbound={false}
							className={classes.flight}
							dataCy="OfferSummary-inboundFlight"
							showFlightDetails={true}
						/>
					</div>
					<div className={cx(classes.flightExtras, classes.flightSection)}>
						<OfferItem
							variant="bold"
							iconType="luggage"
							title={luggageText}
							dataCy="OfferSummary-luggage"
						/>
						<OfferItem
							variant="bold"
							iconType="transfer"
							title={transferText}
							dataCy="OfferSummary-transfer"
						/>
					</div>
				</div>
			)}
		</>
	);

	return (
		<div className={classes.offerSummaryWrapper}>
			<OfferSummaryTemplate accommodation={accommodation} topBlock={<TopBlock />} bottomBlock={<BottomBlock />} />
			{withPriceOverview && (
				<OfferSummaryWrapper
					isCheckingAvailability={isCheckingAvailability}
					checkAvailability={checkAvailability}
					tourOperator={tourOperator}
					isAccoOnlyOffer={isAccoOnly}
				>
					{children}
				</OfferSummaryWrapper>
			)}
		</div>
	);
};
